import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Goals cell for the scheduler panel — lists the user's active personal goals
// and lets them block time toward one. Two paths:
//   • "+ Add to calendar" → opens the block editor for a single Personal block
//     (unchanged; goals recur, so this is a per-day quick action).
//   • "Plan week" → a smart popover that suggests a weekly target and drops a
//     Personal block on each chosen day in one action. Quantity goals fold a
//     per-session amount into the block title ("Run 750 miles — 6 mi"); streak
//     goals just block the days so the chain can't slip.
import { useEffect, useState } from "react";
import { api } from "../../api/client";
// Resolve a quantity (counter / % growth) goal into a suggested weekly target
// + this-week progress, all in real units. null for streaks / untargeted.
function quantityPlan(g) {
    if (g.target_type === "habit")
        return null;
    let base = 0;
    const cur = g.current_value;
    let tgt = 0;
    if (g.target_type === "percentage") {
        if (g.baseline_value != null && g.target_value != null && g.target_value > 0) {
            base = g.baseline_value;
            tgt = base * (1 + g.target_value / 100);
        }
        else {
            tgt = g.target_value && g.target_value > 0 ? g.target_value : 100;
        }
    }
    else {
        tgt = g.target_value ?? 0;
    }
    if (!tgt || tgt <= 0)
        return null;
    const remaining = Math.max(0, Math.round((tgt - cur) * 10) / 10);
    const unit = g.unit?.trim() || "";
    // Whole-unit goals shouldn't show fractional counts.
    const whole = [g.target_value, g.current_value, g.baseline_value]
        .filter((v) => v != null)
        .every((v) => Number.isInteger(v));
    let weekly = 0;
    let cadence = null;
    if (g.target_date && remaining > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const [y, m, d] = g.target_date.split("-").map(Number);
        const end = new Date(y, (m || 1) - 1, d || 1);
        const daysLeft = Math.round((end.getTime() - today.getTime()) / 86400000);
        if (daysLeft > 0) {
            const perWeek = remaining / Math.max(1, daysLeft / 7);
            weekly = Math.max(0, Math.round(perWeek * 10) / 10);
            if (perWeek >= 0.95) {
                cadence = {
                    value: whole ? Math.max(1, Math.ceil(perWeek)) : Math.round(perWeek * 10) / 10,
                    period: "week",
                };
            }
            else {
                const perMonth = remaining / Math.max(1, daysLeft / 30.4);
                cadence = {
                    value: whole ? Math.max(1, Math.ceil(perMonth)) : Math.round(perMonth * 10) / 10,
                    period: "month",
                };
            }
        }
    }
    return { weekly, unit, thisWeek: g.this_week_value ?? 0, remaining, cadence };
}
// How many whole weeks fall (at least partly) within the month containing
// `monthStartIso` — used to derive a weekly sub-target from a monthly
// milestone. Mon-first, matching the rest of the app.
function weeksInMonth(monthStartIso) {
    const [y, m] = monthStartIso.split("-").map(Number);
    const start = new Date(y, (m || 1) - 1, 1);
    const monthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    const firstMon = new Date(start);
    firstMon.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    let count = 0;
    const cur = new Date(firstMon);
    while (cur.getTime() <= monthEnd.getTime() && count < 6) {
        count += 1;
        cur.setDate(cur.getDate() + 7);
    }
    return Math.max(1, count);
}
// "This week" line text for a goal WITH a milestone breakdown — the user's
// ACTUAL weekly target, not an averaged cadence. Returns null when the goal
// has no breakdown / no current milestone (caller then falls back to the
// averaged cadence text).
//   • weekly breakdown → current milestone's target/actual directly.
//   • monthly breakdown → weekly target = round(month target / weeks in
//     month) (min 1); actual = goal.this_week_value.
function weeklyTargetLine(g) {
    if (!g.milestone_period || !g.milestones || g.milestones.length === 0) {
        return null;
    }
    const current = g.milestones.find((m) => m.is_current);
    if (!current)
        return null;
    const unit = g.unit?.trim();
    const unitSuffix = unit ? ` ${unit}` : "";
    const r = (n) => Math.round(n * 10) / 10;
    if (g.milestone_period === "week") {
        return `This week: ${r(current.actual)} / ${r(current.target)}${unitSuffix}`;
    }
    // monthly
    const n = weeksInMonth(current.period_start);
    const weeklyTarget = Math.max(1, Math.round(current.target / n));
    const actual = g.this_week_value ?? 0;
    return `This week: ${r(actual)} / ${weeklyTarget}${unitSuffix}`;
}
// The next `n` days starting today (00:00 local), for the day picker.
function nextDays(n) {
    const out = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    for (let i = 0; i < n; i++) {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        out.push(d);
    }
    return out;
}
function dayKey(d) {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function dayLabel(d, i) {
    if (i === 0)
        return "Today";
    if (i === 1)
        return "Tmrw";
    return d.toLocaleDateString(undefined, { weekday: "short" });
}
// Time-of-day options for the "Plan week" picker — every 30 min, 6am–9pm,
// as { value:"HH:MM", label:"7:00 AM" }. Covers a normal working window.
const TIME_OPTIONS = (() => {
    const out = [];
    for (let h = 6; h <= 21; h++) {
        for (const m of [0, 30]) {
            const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
            const ampm = h < 12 ? "AM" : "PM";
            const h12 = h % 12 === 0 ? 12 : h % 12;
            out.push({ value, label: `${h12}:${String(m).padStart(2, "0")} ${ampm}` });
        }
    }
    return out;
})();
export function SchedulerGoalsCell({ expanded, onToggle, onSchedule, onScheduleDays, }) {
    const [goals, setGoals] = useState([]);
    const [loaded, setLoaded] = useState(false);
    // Which goal's "Plan week" panel is open (goal id), plus its working state.
    const [planId, setPlanId] = useState(null);
    const [picked, setPicked] = useState(new Set());
    const [perSession, setPerSession] = useState("");
    // Time of day each scheduled session lands on (applied to every chosen day).
    const [time, setTime] = useState("09:00");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    async function load() {
        try {
            const rows = await api.get("/me/goals");
            setGoals(rows.filter((g) => g.category === "personal" && g.status === "active"));
        }
        catch {
            /* transient — leave prior list */
        }
        finally {
            setLoaded(true);
        }
    }
    useEffect(() => {
        if (expanded && !loaded)
            void load();
    }, [expanded, loaded]);
    const days = nextDays(7);
    function openPlan(g) {
        setPlanId(g.id);
        setPicked(new Set());
        setResult(null);
        const qp = quantityPlan(g);
        // Seed the per-session amount at roughly a third of the weekly target so
        // a 3-day week lands on pace; the user can tune it.
        setPerSession(qp && qp.weekly > 0 ? String(Math.max(1, Math.round(qp.weekly / 3))) : "");
    }
    function toggleDay(d) {
        const k = dayKey(d);
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(k))
                next.delete(k);
            else
                next.add(k);
            return next;
        });
    }
    async function confirmPlan(g) {
        const chosen = days.filter((d) => picked.has(dayKey(d)));
        if (chosen.length === 0)
            return;
        const isHabit = g.target_type === "habit";
        const amt = perSession ? Number(perSession) : null;
        setBusy(true);
        setResult(null);
        try {
            const r = await onScheduleDays(g, chosen, {
                perSession: isHabit ? null : amt,
                durationMinutes: isHabit ? 15 : 30,
                time,
            });
            const noun = isHabit ? "day" : "session";
            let msg = `Added ${r.scheduled} ${noun}${r.scheduled === 1 ? "" : "s"} to your calendar.`;
            if (r.failed > 0)
                msg += ` ${r.failed} slot${r.failed === 1 ? "" : "s"} were busy — pick another time.`;
            setResult(msg);
            setPicked(new Set());
        }
        catch {
            setResult("Couldn't schedule — try again.");
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("div", { className: `schedulable-widget ${expanded ? "expanded" : "collapsed"}`, children: [_jsxs("button", { type: "button", className: "schedulable-widget-head schedulable-widget-toggle", onClick: onToggle, "aria-expanded": expanded, children: [_jsxs("h4", { children: ["Goals", " ", goals.length > 0 && (_jsxs("span", { className: "muted", style: { fontWeight: 400 }, children: ["\u00B7 ", goals.length] }))] }), _jsx("span", { className: "widget-toggle-icon", "aria-hidden": "true", children: expanded ? "−" : "+" })] }), expanded && (_jsxs(_Fragment, { children: [_jsx("p", { className: "muted small scheduler-claim-hint", children: "Block time toward an active personal goal. \"Plan week\" suggests a weekly target and drops sessions on the days you pick." }), loaded && goals.length === 0 && (_jsx("div", { className: "muted small", children: "No active personal goals. Set one on the Goals page." })), goals.length > 0 && (_jsx("ul", { className: "schedulable-list", children: goals.map((g) => {
                            const isHabit = g.target_type === "habit";
                            const qp = quantityPlan(g);
                            const isPlanOpen = planId === g.id;
                            const chosenCount = days.filter((d) => picked.has(dayKey(d))).length;
                            const amt = perSession ? Number(perSession) : 0;
                            // For a goal WITH a milestone breakdown, the suggestion line is
                            // the user's actual weekly target (same source of truth as the
                            // goal card), not the averaged cadence below.
                            const weekLine = isHabit ? null : weeklyTargetLine(g);
                            return (_jsxs("li", { className: "schedulable-item goal-sched-item", children: [_jsx("div", { className: "item-title", children: g.title }), isHabit ? (_jsx("div", { className: "item-meta", children: _jsxs("span", { className: "muted", children: [Math.min(7, Math.round(g.this_week_value ?? 0)), " / 7 days this week \u00B7 keep the chain going"] }) })) : weekLine ? (_jsx("div", { className: "item-meta", children: _jsx("span", { className: "muted", children: weekLine }) })) : qp && qp.cadence ? (_jsx("div", { className: "item-meta", children: _jsxs("span", { className: "muted", children: ["Aim ", qp.cadence.value, qp.unit ? ` ${qp.unit}` : "", "/", qp.cadence.period, " \u00B7", " ", Math.round(qp.thisWeek * 10) / 10, " done this week"] }) })) : (g.why && (_jsx("div", { className: "item-meta", children: _jsx("span", { className: "muted", children: g.why }) }))), _jsxs("div", { className: "item-actions", children: [_jsx("button", { type: "button", className: "btn-secondary btn-small", onClick: (e) => {
                                                    e.stopPropagation();
                                                    if (isPlanOpen)
                                                        setPlanId(null);
                                                    else
                                                        openPlan(g);
                                                }, title: "Plan a few sessions across this week", children: isPlanOpen ? "Close" : "Plan week" }), _jsx("button", { type: "button", className: "btn-primary btn-small", onClick: (e) => {
                                                    e.stopPropagation();
                                                    onSchedule(g);
                                                }, title: "Block one session toward this today", children: "+ Add to calendar" })] }), isPlanOpen && (_jsxs("div", { className: "goal-plan", children: [!isHabit && (_jsxs("label", { className: "goal-plan-amt", children: ["Per session", _jsx("input", { type: "number", min: 0, value: perSession, onChange: (e) => setPerSession(e.target.value), onClick: (e) => e.stopPropagation() }), qp?.unit && (_jsx("span", { className: "muted small", children: qp.unit }))] })), _jsxs("label", { className: "goal-plan-amt", children: ["At", _jsx("select", { value: time, onChange: (e) => setTime(e.target.value), onClick: (e) => e.stopPropagation(), children: TIME_OPTIONS.map((t) => (_jsx("option", { value: t.value, children: t.label }, t.value))) }), _jsx("span", { className: "muted small", children: "each day" })] }), _jsx("div", { className: "goal-plan-days", children: days.map((d, i) => {
                                                    const k = dayKey(d);
                                                    const on = picked.has(k);
                                                    return (_jsxs("button", { type: "button", className: `goal-day-chip ${on ? "active" : ""}`, onClick: (e) => {
                                                            e.stopPropagation();
                                                            toggleDay(d);
                                                        }, children: [_jsx("span", { className: "goal-day-dow", children: dayLabel(d, i) }), _jsx("span", { className: "goal-day-num", children: d.getDate() })] }, k));
                                                }) }), !isHabit &&
                                                qp &&
                                                qp.weekly > 0 &&
                                                chosenCount > 0 &&
                                                amt > 0 && (_jsxs("div", { className: "goal-plan-summary", children: [chosenCount, " \u00D7 ", amt, qp.unit ? ` ${qp.unit}` : "", " =", " ", _jsxs("strong", { children: [Math.round(chosenCount * amt * 10) / 10, qp.unit ? ` ${qp.unit}` : ""] }), " ", "this week \u00B7", " ", Math.round(((chosenCount * amt) / qp.weekly) * 100), "% of your weekly target"] })), isHabit && chosenCount > 0 && (_jsxs("div", { className: "goal-plan-summary", children: ["Locking in", " ", _jsxs("strong", { children: [chosenCount, " day", chosenCount === 1 ? "" : "s"] }), " ", "\u2014 schedule a few at once so the streak can't slip."] })), _jsx("button", { type: "button", className: "btn-primary btn-small goal-plan-go", disabled: busy || chosenCount === 0, onClick: (e) => {
                                                    e.stopPropagation();
                                                    void confirmPlan(g);
                                                }, children: busy
                                                    ? "Scheduling…"
                                                    : isHabit
                                                        ? `Add ${chosenCount || ""} day${chosenCount === 1 ? "" : "s"}`.trim()
                                                        : `Schedule ${chosenCount || ""} session${chosenCount === 1 ? "" : "s"}`.trim() }), result && (_jsx("div", { className: "goal-plan-result muted small", children: result }))] }))] }, g.id));
                        }) }))] }))] }));
}
